/** Las piezas pequeñas que se repiten: etiquetas, estados vacíos, diálogos y avisos. */
import { useEffect, type ReactNode } from 'react'

import { ESTADO_SOLICITUD, ESTADO_TAREA, PRIORIDAD } from '../textos'
import type { EstadoSolicitud, EstadoTarea, Prioridad } from '../tipos'

export function EtiquetaDePrioridad({ valor }: { valor: Prioridad | null }) {
  if (!valor) return <span className="etiqueta">Sin decidir</span>
  return (
    <span className={`etiqueta prio--${valor}`}>
      <span className="punto" aria-hidden="true" />
      {PRIORIDAD[valor]}
    </span>
  )
}

/** El color de un estado, según lo que significa: abierto, en marcha, esperando, hecho o descartado. */
function tonoDe(texto: string): string {
  const t = texto.toLowerCase()
  if (/complet|hech|cerrad|finaliz/.test(t)) return 'ok'
  if (/rechaz|cancel|archiv/.test(t)) return 'descartado'
  if (/falta|bloque|espera|pausa|revisi/.test(t)) return 'aviso'
  if (/cierre/.test(t)) return 'cierre'
  if (/desarrollo|curso|prueba|progreso|activ/.test(t)) return 'marcha'
  if (/aprobad|planific|por hacer|pendiente/.test(t)) return 'previsto'
  if (/nuev/.test(t)) return 'nuevo'
  return ''
}

export function EtiquetaDeEstado({ texto }: { texto: string }) {
  const tono = tonoDe(texto)
  return <span className={`etiqueta${tono ? ` estado--${tono}` : ''}`}>{texto}</span>
}

export function estadoSolicitud(valor: EstadoSolicitud) {
  return ESTADO_SOLICITUD[valor] ?? valor
}

export function estadoTarea(valor: EstadoTarea) {
  return ESTADO_TAREA[valor] ?? valor
}

export function Cargando({ filas = 3 }: { filas?: number }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="cargando">Cargando…</span>
      {Array.from({ length: filas }, (_, i) => (
        <div className="esqueleto" key={i} />
      ))}
    </div>
  )
}

export function Vacio({ titulo, children, icono }: { titulo: string; children?: ReactNode; icono?: string }) {
  return (
    <div className="vacio">
      {icono && <div className="vacio__icono" aria-hidden="true">{icono}</div>}
      <div className="vacio__titulo">{titulo}</div>
      {children}
    </div>
  )
}

export function Error({ children }: { children: ReactNode }) {
  return (
    <div className="error" role="alert">
      {children}
    </div>
  )
}

export function Dialogo({
  titulo,
  children,
  pie,
  alCerrar,
}: {
  titulo: string
  children: ReactNode
  pie?: ReactNode
  alCerrar: () => void
}) {
  // Escape cierra: es lo que cualquiera intenta primero.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar()
    }
    document.addEventListener('keydown', alPulsar)
    return () => document.removeEventListener('keydown', alPulsar)
  }, [alCerrar])

  return (
    <div className="velo" onMouseDown={(e) => e.target === e.currentTarget && alCerrar()}>
      <div className="dialogo" role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="dialogo__cabecera">
          <h2>{titulo}</h2>
          <button
            className="boton boton--plano"
            style={{ marginLeft: 'auto' }}
            onClick={alCerrar}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="dialogo__cuerpo">{children}</div>
        {pie && <div className="dialogo__pie">{pie}</div>}
      </div>
    </div>
  )
}

/** Un círculo con la inicial y un color propio de cada persona: se reconoce de un vistazo. */
export function Avatar({ nombre, grande = false }: { nombre: string; grande?: boolean }) {
  let h = 0
  for (const c of nombre) h = (h * 31 + c.charCodeAt(0)) % 360
  return (
    <span
      className={`avatar${grande ? ' avatar--grande' : ''}`}
      style={{ ['--matiz' as string]: h }}
      aria-hidden="true"
    >
      {nombre.trim().charAt(0).toUpperCase() || '?'}
    </span>
  )
}

export function Cifra({
  valor,
  etiqueta,
  alerta = false,
  tono = '',
}: {
  valor: number
  etiqueta: string
  alerta?: boolean
  /** nuevo, aviso, previsto, marcha, cierre, ok: el mismo color que ese estado. */
  tono?: string
}) {
  return (
    <div className={`tarjeta cifra${tono ? ` cifra--${tono}` : ''}${alerta && valor > 0 ? ' cifra--alerta' : ''}`}>
      <div className="cifra__valor">{valor}</div>
      <div className="cifra__etiqueta">{etiqueta}</div>
    </div>
  )
}
